import { DEFAULT_COUNTRY } from '@/lib/constants/countries';
import type { AddressDetails, StructuredAddress } from './types';

/** A blank structured address — every field genuinely empty. Used to reset state. */
export const EMPTY_STRUCTURED_ADDRESS: StructuredAddress = {
  streetAddress: '',
  suburb: '',
  state: '',
  postcode: '',
  country: ''
};

/**
 * The seed for a *newly started* address: blank except for the default country.
 *
 * Australia is ~99.9% of Vale's addresses, so pre-selecting it means the common case
 * needs no interaction with the country picker at all. Deliberately separate from
 * {@link EMPTY_STRUCTURED_ADDRESS} rather than defaulting that constant — a thing named
 * "empty" must actually be empty.
 */
export const NEW_STRUCTURED_ADDRESS: StructuredAddress = {
  ...EMPTY_STRUCTURED_ADDRESS,
  country: DEFAULT_COUNTRY
};

/**
 * Compose the single-line street address from the provider's discrete number
 * parts, using the Australian convention `{unit}/{streetNumber} {street}`
 * (e.g. `12/34 Smith Street`). Falls back gracefully when any part is absent.
 */
function buildStreetAddress({
  unitNumber,
  streetNumber,
  street
}: Pick<AddressDetails, 'unitNumber' | 'streetNumber' | 'street'>): string {
  const streetPart = [streetNumber, street].filter(Boolean).join(' ').trim();

  if (unitNumber && streetNumber) {
    return `${unitNumber}/${streetPart}`;
  }
  if (unitNumber) {
    return [unitNumber, street].filter(Boolean).join(' ').trim();
  }
  return streetPart;
}

/**
 * Map the backend `AddressDetails` shape onto the structured fields every
 * internal consumer stores. The backend already returns `state` as its short
 * code (e.g. `NSW`) and `country` as its long name (e.g. `Australia`).
 */
export function mapAddressDetailsToStructured(
  details: AddressDetails
): StructuredAddress {
  return {
    streetAddress: buildStreetAddress(details),
    suburb: details.suburb ?? '',
    state: details.state ?? '',
    postcode: details.postcode ?? '',
    country: details.country ?? '',
    formattedAddress: details.formattedAddress ?? ''
  };
}

/** True when at least one structured field carries a value. */
export function hasAddressValue(value: StructuredAddress): boolean {
  return Boolean(
    value.streetAddress ||
    value.suburb ||
    value.state ||
    value.postcode ||
    value.country
  );
}
